import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { SrIcon } from "@/components/ui/SrIcon";
import { toast } from "sonner";
import { useAssignableUsers, type UserLookup } from "@/hooks/use-users";
import { useWidgetConfigs } from "@/hooks/use-widget-configs";
import {
  useUserWidgetOverrides,
  useUpsertUserWidgetOverride,
  useDeleteUserWidgetOverride,
  type UserWidgetOverride,
} from "@/hooks/use-user-widget-overrides";
import {
  WIDGET_DEFINITIONS,
  DEFAULT_ROLE_CONFIGS,
  type RoleWidgetConfig,
} from "@/lib/widget-registry";

/**
 * PerUserOverridesPanel — admin UI for adjusting widget visibility and
 * display order on a per-employee basis. Overrides are stored as deltas
 * over the role default; clearing an override returns that widget to
 * its role-default state.
 */
export function PerUserOverridesPanel() {
  const { data: users = [], isLoading: loadingUsers } = useAssignableUsers();
  const { data: dbConfigs } = useWidgetConfigs();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedUserId) ?? null,
    [users, selectedUserId],
  );

  if (loadingUsers) {
    return (
      <div className="flex items-center justify-center py-10">
        <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-muted-foreground">Ansatt:</span>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" className="h-8">
              {selectedUser?.full_name ?? selectedUser?.email ?? "Velg ansatt…"}
              <SrIcon name="chevrons-up-down" className="w-3.5 h-3.5 ml-2 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <Command>
              <CommandInput placeholder="Søk ansatte…" />
              <CommandList>
                <CommandEmpty>Ingen treff</CommandEmpty>
                <CommandGroup>
                  {users.map((u) => (
                    <CommandItem
                      key={u.id}
                      value={`${u.full_name ?? ""} ${u.email}`}
                      onSelect={() => {
                        setSelectedUserId(u.id);
                        setPickerOpen(false);
                      }}
                      className="text-xs"
                    >
                      <span className="flex-1">{u.full_name ?? u.email}</span>
                      <Badge variant="outline" className="text-[10px] h-4 ml-2">
                        {u.role}
                      </Badge>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {selectedUser && (
          <Badge variant="secondary" className="text-xs">
            Rolle: {selectedUser.role}
          </Badge>
        )}
      </div>

      {selectedUser ? (
        <UserOverrideTable user={selectedUser} dbConfigs={dbConfigs} />
      ) : (
        <p className="text-sm text-muted-foreground py-6">
          Velg en ansatt for å overstyre widget-synlighet og rekkefølge for vedkommende.
        </p>
      )}
    </div>
  );
}

interface UserOverrideTableProps {
  user: UserLookup;
  dbConfigs: RoleWidgetConfig[] | undefined;
}

function UserOverrideTable({ user, dbConfigs }: UserOverrideTableProps) {
  const { data: overrides = [], isLoading } = useUserWidgetOverrides(user.id);
  const upsert = useUpsertUserWidgetOverride();
  const remove = useDeleteUserWidgetOverride();

  // Resolve role default for the user: prefer DB-saved config, fall back to hardcoded default.
  const roleConfigs = dbConfigs && dbConfigs.length > 0 ? dbConfigs : DEFAULT_ROLE_CONFIGS;
  const roleDefault =
    roleConfigs.find((c) => c.role === user.role) ??
    DEFAULT_ROLE_CONFIGS.find((c) => c.role === "user") ??
    DEFAULT_ROLE_CONFIGS[0];

  const overrideMap = useMemo(() => {
    const m = new Map<string, UserWidgetOverride>();
    for (const o of overrides) m.set(o.widget_id, o);
    return m;
  }, [overrides]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  async function handleToggle(widgetKey: string, currentVisible: boolean, defaultOrder: number) {
    const newVisible = !currentVisible;
    try {
      await upsert.mutateAsync({
        user_id: user.id,
        widget_id: widgetKey,
        visible: newVisible,
        display_order: overrideMap.get(widgetKey)?.display_order ?? defaultOrder,
      });
    } catch (err) {
      toast.error(`Kunne ikke lagre: ${(err as Error).message}`);
    }
  }

  async function handleOrderChange(
    widgetKey: string,
    nextOrder: number | null,
    currentVisible: boolean,
  ) {
    try {
      await upsert.mutateAsync({
        user_id: user.id,
        widget_id: widgetKey,
        visible: currentVisible,
        display_order: nextOrder,
      });
    } catch (err) {
      toast.error(`Kunne ikke lagre: ${(err as Error).message}`);
    }
  }

  async function handleReset(widgetKey: string) {
    try {
      await remove.mutateAsync({ user_id: user.id, widget_id: widgetKey });
    } catch (err) {
      toast.error(`Kunne ikke nullstille: ${(err as Error).message}`);
    }
  }

  // Build a complete list: role-default widgets first (sorted), then any override
  // whose widget is not part of the role default (rare, but possible if role
  // changed after override was set — admin should still be able to clear them).
  const roleKeys = new Set(roleDefault.widgets.map((w) => w.key));
  const rows = [
    ...[...roleDefault.widgets]
      .sort((a, b) => a.order - b.order)
      .map((w) => ({
        key: w.key,
        defaultOrder: w.order,
        inRoleDefault: true,
      })),
    ...overrides
      .filter((o) => !roleKeys.has(o.widget_id))
      .map((o) => ({
        key: o.widget_id,
        defaultOrder: o.display_order ?? 999,
        inRoleDefault: false,
      })),
  ];

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6">
        Ingen widgets tilgjengelig for rollen "{user.role}".
      </p>
    );
  }

  return (
    <div className="border rounded-md overflow-hidden">
      <div className="grid grid-cols-[1fr_80px_100px_120px_auto] gap-2 px-3 py-2 bg-muted/40 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <span>Widget</span>
        <span className="text-center">Vis</span>
        <span>Rekkefølge</span>
        <span>Status</span>
        <span></span>
      </div>
      <div className="divide-y">
        {rows.map((row) => {
          const def = WIDGET_DEFINITIONS.find((d) => d.key === row.key);
          const override = overrideMap.get(row.key);
          // Effective visibility: override wins; otherwise role default is "visible".
          const isVisible = override ? override.visible : row.inRoleDefault;
          const effectiveOrder = override?.display_order ?? row.defaultOrder;
          const hasOverride = !!override;

          return (
            <div
              key={row.key}
              className="grid grid-cols-[1fr_80px_100px_120px_auto] gap-2 px-3 py-2 items-center"
            >
              <div>
                <div className="text-sm font-medium">{def?.name ?? row.key}</div>
                {!row.inRoleDefault && (
                  <div className="text-[10px] text-amber-600">
                    Ikke i rolle-default
                  </div>
                )}
              </div>
              <div className="flex justify-center">
                <Checkbox
                  checked={isVisible}
                  onCheckedChange={() =>
                    handleToggle(row.key, isVisible, row.defaultOrder)
                  }
                  aria-label={`Vis ${def?.name ?? row.key}`}
                />
              </div>
              <Input
                type="number"
                value={effectiveOrder}
                onChange={(e) => {
                  const raw = e.target.value;
                  const next = raw === "" ? null : Number(raw);
                  handleOrderChange(row.key, next, isVisible);
                }}
                className="h-7 text-xs"
              />
              <div>
                {hasOverride ? (
                  <Badge variant="secondary" className="text-[10px]">
                    Overstyrt
                  </Badge>
                ) : (
                  <span className="text-[10px] text-muted-foreground">
                    Rolle-default
                  </span>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleReset(row.key)}
                disabled={!hasOverride || remove.isPending}
                className="h-7 text-xs"
                title="Nullstill til rolle-default"
              >
                <SrIcon name="undo-2" className="w-3.5 h-3.5" />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
